import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { FormError } from '../../../components/hook-form/hook-form-error';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { LoadingIndicator } from '../../../components/loading-indicator.component';

export enum DateFormat {
  SIMPLE_DATE = 'MM/DD/YYYY',
}

interface DateLimits {
  maxDateEnabled?: boolean;
  maxDateValue?: string;
  minDateEnabled?: boolean;
  minDateValue?: string;
}

interface DateSelectFieldProps {
  label: string;
  name: string;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  format?: DateFormat;
  required?: boolean;
  dateLimits?: DateLimits;
  className?: string;
  restrictDateSelection?: boolean;
  holidayDateList?: any[];
  isLoading?: boolean;
}

const convertToDate = (value: any) => {
  if (!value || value === 'Invalid Date') return null;
  return dayjs(value).isValid() ? dayjs(value) : null;
};

export const FormDateField = ({
  name,
  label,
  required,
  format = DateFormat.SIMPLE_DATE,
  disabled,
  onChange,
  dateLimits = {},
  restrictDateSelection,
  className,
  holidayDateList,
  isLoading
}: DateSelectFieldProps) => {
  const { control, getValues, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  // const {
  //   maxDateEnabled = false,
  //   maxDateValue = dateLimits?.maxDateValue ?? '',
  //   minDateEnabled = false,
  //   minDateValue = dateLimits?.minDateValue ?? '',
  // } = dateLimits;

  // useWatch({
  //   name: [maxDateValue, minDateValue] || [],
  // });

  const handleChange = (date: any) => {
    if (!date) {
      setValue(name, "");
      return;
    }

    const selectedDate = dayjs(date, format, true);

    const year = selectedDate.year();

    const formattedDate =
      selectedDate.isValid() && year >= 1900 && year <= 2100
        ? selectedDate.format(format)
        : "";

    const day =
      selectedDate.isValid() && year >= 1900 && year <= 2100
        ? selectedDate.day()
        : -1;

    if (restrictDateSelection && (day === 0 || day === 6)) {
      setValue(name, "");
      showSnackbar(
        'Weekend dates are not allowed. Please choose a weekday.',
        'warning'
      );
      return;
    }

    if (holidayDateList?.includes(formattedDate)) {
      setValue(name, "");
      showSnackbar(
        'Selected date is a holiday. Please choose another date.',
        'warning'
      );
      return;
    }

    if (onChange) {
      onChange(date);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        control={control}
        name={name}
        rules={{
          required: required ? `${label} is required` : false, // Apply required validation
        }}
        render={({ field: { onChange: fieldOnChange, value } }) => (
          <>
            <LoadingIndicator isLoading={isLoading} />
            <DatePicker
              onChange={(date) => {
                const rawDate = convertToDate(date);
                fieldOnChange(rawDate); // Pass raw date to react-hook-form
                handleChange(formatSiTrackDate(rawDate, format)); // Call external onChange with formatted value
              }}
              value={convertToDate(value)}
              shouldDisableDate={(date) => {
                    const formattedDate = dayjs(date).format(format);

                    const isWeekend =
                        (dayjs(date).day() === 0 || dayjs(date).day() === 6);

                    const isHoliday =
                        holidayDateList?.some(
                            (h: any) => h.date === formattedDate
                        ) ?? false;
                    return !!restrictDateSelection && (isWeekend || isHoliday);
              }}
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                    size: 'small',
                    label: `${label}`,
                    className: `${className} ${required ? 'itsarequiredField ' : ''} ${value ? 'has-data' : ''} custDateQB`,
                },
              //  day: (ownerState: any) => {
              //    const date = ownerState.day;
              //    const formattedDate = dayjs(date).format(format);

              //    // const isWeekend =
              //    //   !!restrictDateSelection &&
              //    //   (dayjs(date).day() === 0 || dayjs(date).day() === 6);

              //    const isHoliday =
              //      holidayDateList?.includes(formattedDate) ?? false;

              //    // const isDisabledCustom = isWeekend || isHoliday;
              //    const isDisabledCustom = isHoliday;

              //    return {
              //      sx: isDisabledCustom
              //        ? {
              //          backgroundColor: '#FFA2A2 !important',
              //          color: 'black !important',
              //          opacity: 1, // override default faded look
              //        }
              //        : {},
              //    };
              //  },
              }}
              disabled={disabled}
            />
            <FormError name={name} />
          </>
        )}
      />
    </LocalizationProvider>
  );
};

FormDateField.defaultProps = {
  label: undefined,
  onChange: () => { },
};

export default FormDateField;

// Helper function to format dates
export const formatSiTrackDate = (value: any, format: DateFormat) => {
  if (!value) return '';

  return dayjs(value).format(format);
};
